import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthProvider } from '#global/context/auth/AuthProvider.tsx';
import { GlobalStateProvider } from '#global/context/global-state/GlobalStateProvider.tsx';
import { NotificationsProvider } from '#global/context/notifications/NotificationsProvider.tsx';
import { Oops } from '#screens/errors/Oops.tsx';

type Props = {
  children: React.ReactNode;
  client: QueryClient;
};

/**
 * {@link ContextProviders} sets up all of our top-level react context providers that are needed
 * for the app.  By keeping them out of the App itself, we are more easily able to test
 * the App component with alternate context providers geared towards testing.
 */
export function ContextProviders({ children, client }: Props) {
  return (
    <Suspense>
      <ErrorBoundary
        fallbackRender={(props) => (
          // Do not spread in 'resetErrorBoundary', because we want a full page reload on retry
          <Oops error={props.error as unknown} showErrorDetails={false} className="height-100vh" />
        )}
      >
        <QueryClientProvider client={client}>
          <GlobalStateProvider>
            <NotificationsProvider>
              <AuthProvider>{children}</AuthProvider>
            </NotificationsProvider>
          </GlobalStateProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </Suspense>
  );
}
